import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { API_BASE } from "../api/config";
import "./ExpenseCategories.css";

export default function ExpenseCategories() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({
    categoryName: "",
    description: "",
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  function updateForm(field, value) {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  async function loadCategories() {
    try {
      setLoading(true);
      setError("");

      const res = await fetch(`${API_BASE}/api/erp/expense-categories`);
      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to load expense categories");
      }

      setRows(data.rows || []);
    } catch (err) {
      setError(err.message || "Expense category loading failed");
    } finally {
      setLoading(false);
    }
  }

  function editCategory(row) {
    setForm({
      categoryName: row.category_name || "",
      description: row.description || "",
    });

    setSuccessMsg(`Selected ${row.category_name}`);
  }

  async function saveCategory(e) {
    e.preventDefault();

    try {
      setSaving(true);
      setError("");
      setSuccessMsg("");

      if (!form.categoryName.trim()) {
        throw new Error("Category name required");
      }

      const res = await fetch(`${API_BASE}/api/erp/expense-categories`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          categoryName: form.categoryName.trim(),
          description: form.description.trim(),
        }),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to save category");
      }

      setSuccessMsg("Expense category saved successfully.");

      setForm({
        categoryName: "",
        description: "",
      });

      await loadCategories();
    } catch (err) {
      setError(err.message || "Expense category save failed");
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    loadCategories();
  }, []);

  return (
    <Layout title="Expense Categories">
      <div className="expenseCat">
        <div className="expenseCat__hero">
          <div>
            <div className="expenseCat__eyebrow">ERP Expense Control</div>
            <h1>Expense Category Manage</h1>
            <p>
              Create and manage cash-out categories for shop expenses, refunds,
              delivery costs, bank charges and other business expenses.
            </p>
          </div>

          <button onClick={loadCategories} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>

        {error && <div className="expenseCat__error">{error}</div>}
        {successMsg && <div className="expenseCat__success">{successMsg}</div>}

        <div className="expenseCat__grid">
          <div className="expensePanel">
            <h3>Add / Update Category</h3>

            <form className="expenseForm" onSubmit={saveCategory}>
              <label>
                Category Name
                <input
                  value={form.categoryName}
                  onChange={(e) => updateForm("categoryName", e.target.value)}
                  placeholder="Example: Marketing Expense"
                />
              </label>

              <label>
                Description
                <textarea
                  value={form.description}
                  onChange={(e) => updateForm("description", e.target.value)}
                  placeholder="Write description"
                />
              </label>

              <button type="submit" disabled={saving}>
                {saving ? "Saving..." : "Save Category"}
              </button>
            </form>
          </div>

          <div className="expensePanel">
            <h3>Default Category Guide</h3>

            <div className="expenseGuide">
              <div>
                <span>Delivery Expense</span>
                <strong>Royal Express / courier / transport cost</strong>
              </div>

              <div>
                <span>Refund / Return</span>
                <strong>Customer refund and sale return cash out</strong>
              </div>

              <div>
                <span>Bank Charges</span>
                <strong>Bank fee and payment transaction charges</strong>
              </div>

              <div>
                <span>Other Expense</span>
                <strong>General cash-out not included in other categories</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="expensePanel expensePanel--table">
          <div className="expensePanel__tableHead">
            <div>
              <h3>Expense Category List</h3>
              <p>Click a row to edit category name and description.</p>
            </div>
          </div>

          <div className="expenseTableWrap">
            <table className="expenseTable">
              <thead>
                <tr>
                  <th>Category Name</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="expenseTable__empty">
                      No expense categories.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.id} onClick={() => editCategory(row)}>
                      <td>
                        <span className="expenseName">
                          {row.category_name}
                        </span>
                      </td>
                      <td>{row.description || "-"}</td>
                      <td>
                        <span
                          className={
                            row.active
                              ? "expenseBadge expenseBadge--active"
                              : "expenseBadge expenseBadge--inactive"
                          }
                        >
                          {row.active ? "ACTIVE" : "INACTIVE"}
                        </span>
                      </td>
                      <td>{String(row.created_at || "").slice(0, 19)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
